import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { CLIENT_ID_QUERY_KEY } from '@/consts/const';
import { useFetch } from '@/hooks/useFetch';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import type { GetAirRequestsResponse } from '@/types/airRequestApiTypes';
import { AIR_REQUESTS_QUERY_KEY } from '../../consts/client-detail';

interface UseGetAirRequestsInfiniteQueryProps {
  status?: 1 | 2; // 1: Draft, 2: Submitted
  search?: string;
  perPage?: number;
  enabled?: boolean;
  clientId?: number;
}

export const useGetAirRequestsInfiniteQuery = ({
  status = 1, // By default, we only show drafts (Draft)
  search,
  perPage = 24,
  enabled = true,
  clientId: clientIdParam,
}: UseGetAirRequestsInfiniteQueryProps = {}) => {
  const [searchQuery] = useSearchQuery();
  const { doFetch } = useFetch<void, GetAirRequestsResponse>();
  const clientId = clientIdParam ?? searchQuery.get(CLIENT_ID_QUERY_KEY);

  const buildUrl = useCallback(
    (pageParam: number) => {
      const params = new URLSearchParams();

      if (status) params.append('status', status.toString());
      if (search) params.append('search', search);
      if (perPage) params.append('per_page', perPage.toString());
      if (pageParam) params.append('page', pageParam.toString());

      return `/api/clients/${clientId}/air-requests?${params.toString()}`;
    },
    [clientId, status, search, perPage]
  );

  const queryKey = [AIR_REQUESTS_QUERY_KEY, clientId, status, search, perPage];

  const fetchAirRequests = async ({ pageParam = 1 }) => {
    const url = buildUrl(pageParam);
    const results = await doFetch(url);

    return {
      ...results,
      nextCursor: results.pagination?.last_page !== results.pagination?.current_page ? pageParam + 1 : undefined,
    };
  };

  const queryResult = useInfiniteQuery({
    queryKey,
    queryFn: fetchAirRequests,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 1,
    enabled: enabled && !!clientId,
  });

  return {
    ...queryResult,
    data: queryResult.data?.pages.flatMap((page) => page.data) ?? [],
    pagination: queryResult.data?.pages.at(-1)?.pagination,
  };
};
